import React, {useRef} from "react";
import invariant from "invariant";
import * as redux from "redux";
import { reducer as httpRequest } from "./httpRequest";
import { cleanOnResetOrganization, cleanOnReset } from "./clean";
import { logger } from "redux-logger";
import thunk from "redux-thunk";
import { apiReducer, APIMiddleware, Resolver } from "api";
import { useWithErrorStack } from "api/errorStack";
import { useAuth } from 'react-oidc-context';

const resolver = new Resolver(window.weezConfig.api.url, {
	unsplash: 'https://api.unsplash.com',
});

// Let jest a chance to mock fetch in the test env
const fetch_ = (process.env.NODE_ENV === "test") ? (url, options) => global.fetch(url, options): global.fetch;

const reducers = [
	// Reset when leaving an orga or login out
	cleanOnResetOrganization,
	cleanOnReset,

	apiReducer,

	// Combine reducer functions act each on its state key
	wrapperReducer(redux.combineReducers({
		httpRequest,
	})),
];

/**
 * Function to wrap the combineReducers and set the new state with the previousState. We do that because the function 'combineReducers' create a new state and delete the existing state
 * @param  {function} combineReducers - The reducers that are combined
 * @return {function} - A reducer function
 */
function wrapperReducer(combinedReducers) {
	return (state, action) => {
		const nextState = combinedReducers(state, action);
		if (nextState === state) {
			return state;
		}
		return Object.assign({}, state, nextState);
	};
}

/**
 * Apply each reduce function to the state
 * @param {Object} state - The state
 * @param {Object} action - The action
 * @returns {Object} The state
 */
function reducerFn(state, action) {
	return reducers.reduce((previousState, reducer) => reducer(previousState, action), state);
}

function useAuthenticationMiddleware() {
	const { user } = useAuth();
	const ref = useRef(user.access_token);
	ref.current = user.access_token;
	const tokenType  = 'Bearer';


	return fetch => {
		// Create a fetch alias that injects the token
		return (url, opts) => {
			opts = opts || {};
			const headers = (opts.headers = opts.headers || {});
			if (!opts.noAuth && !headers.Authorization) headers.Authorization = `${tokenType} ${ref.current}`;
			return fetch(url, opts);
		};
	};
}

/**
 * Create a configured store instance
 * @returns {Object} a store
 */
export function useStore(inital) {
	const ref = React.useRef(null);
	const authMiddleware = useAuthenticationMiddleware();
	const withErrorStack = useWithErrorStack();

	if (ref.current === null) {
		const draxAPIMiddleware = APIMiddleware(fetch_, APIMiddleware.connectors.concat([
			authMiddleware,
			withErrorStack,
			resolver.asMiddleware(),
		]));

		const extraMiddlewares = (process.env.NODE_ENV === "development") ? [logger] : [];

		ref.current = redux.createStore(
			reducerFn,
			inital,
			redux.applyMiddleware(thunk, draxAPIMiddleware, ...extraMiddlewares),
		);
	}

	invariant(ref.current, 'undefined store');
	return ref.current;
}

export { resolver };
